import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Button, Flex, Heading, LoadingOrbit, Switch, Text } from '@chatsdet/ui';
import { Warning } from '@phosphor-icons/react';
import { API_URL } from '../../../config.js';
import { toApiError } from '../../../lib/api-error';

type ObservabilitySettings = {
  traceHeadersApiEnabled: boolean;
  traceHeadersBrowserEnabled: boolean;
};

type ObservabilitySectionProps = {
  isAdmin: boolean;
};

export function ObservabilitySection({ isAdmin }: ObservabilitySectionProps) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = React.useState<ObservabilitySettings | null>(null);

  const settingsQuery = useQuery({
    queryKey: ['org-observability-settings'],
    queryFn: async (): Promise<{ ok: boolean; data: ObservabilitySettings }> => {
      const response = await fetch(`${API_URL}/org/settings`, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to load observability settings');
      return response.json();
    },
  });

  React.useEffect(() => {
    if (settingsQuery.data?.data) {
      setDraft({
        traceHeadersApiEnabled: settingsQuery.data.data.traceHeadersApiEnabled,
        traceHeadersBrowserEnabled: settingsQuery.data.data.traceHeadersBrowserEnabled,
      });
    }
  }, [settingsQuery.data]);

  const updateMutation = useMutation({
    mutationFn: async (payload: Partial<ObservabilitySettings>) => {
      const response = await fetch(`${API_URL}/org/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw await toApiError(response, 'Failed to update observability settings');
      const data = await response.json();
      return data as { ok: boolean; data: ObservabilitySettings };
    },
    onSuccess: ({ data }) => {
      setDraft({
        traceHeadersApiEnabled: data.traceHeadersApiEnabled,
        traceHeadersBrowserEnabled: data.traceHeadersBrowserEnabled,
      });
      void queryClient.invalidateQueries({ queryKey: ['org-observability-settings'] });
    },
  });

  if (settingsQuery.isLoading) {
    return (
      <Flex align="center" justify="center" className="py-12">
        <LoadingOrbit size="md" />
      </Flex>
    );
  }

  if (settingsQuery.isError || !draft) {
    return (
      <Box className="space-y-6">
        <div className="space-y-1">
          <Heading level={2}>Observability</Heading>
          <Text size="sm" className="text-muted-foreground">
            Failed to load observability settings.
          </Text>
        </div>
        <Flex justify="start">
          <Button variant="outline" onPress={() => settingsQuery.refetch()}>
            Retry
          </Button>
        </Flex>
      </Box>
    );
  }

  const toggleApi = (next: boolean) =>
    setDraft((prev) => (prev ? { ...prev, traceHeadersApiEnabled: next } : prev));

  const toggleBrowser = (next: boolean) =>
    setDraft((prev) => (prev ? { ...prev, traceHeadersBrowserEnabled: next } : prev));

  const enabledCount =
    (draft.traceHeadersApiEnabled ? 1 : 0) + (draft.traceHeadersBrowserEnabled ? 1 : 0);
  const disabled = !isAdmin || updateMutation.isPending;
  const saveSettings = () => updateMutation.mutate(draft);

  return (
    <Box className="space-y-6">
      {/* Page header — outside the card, matches FeatureFlagsSection style */}
      <div className="space-y-1">
        <Heading level={2}>Observability</Heading>
        <Text size="sm" className="text-muted-foreground">
          {enabledCount} of 2 trace header signals enabled for this organization
        </Text>
      </div>

      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <Text
            size="xs"
            weight="semibold"
            className="uppercase tracking-wider text-muted-foreground"
          >
            Trace headers
          </Text>
          <Text size="xs" className="text-muted-foreground tabular-nums">
            {enabledCount} / 2
          </Text>
        </div>

        <Text size="sm" className="text-muted-foreground">
          Attach <code className="rounded bg-muted px-1 py-0.5 text-xs">x-canary-flow-id</code> and{' '}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">x-canary-run-id</code> headers to
          outbound requests so your server logs can correlate canary traffic to a specific run via{' '}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">
            app.trycanary.ai/flows/&#123;flowId&#125;?runId=&#123;runId&#125;
          </code>
          . Header values are opaque UUIDs only — no credentials, no PII.
        </Text>

        <div className="rounded-lg border border-border bg-card divide-y divide-border overflow-hidden">
          {/* API sequence row */}
          <div className="flex items-start justify-between gap-4 px-4 py-3.5 transition-colors hover:bg-muted/40">
            <div className="min-w-0 flex-1">
              <Text weight="medium">API sequence requests</Text>
              <Text size="sm" className="text-muted-foreground mt-1">
                Adds trace headers to every step in an API sequence (server-to-server). No CORS
                impact. Recommended.
              </Text>
            </div>
            <Switch
              aria-label="Toggle API sequence trace headers"
              isDisabled={disabled}
              isSelected={draft.traceHeadersApiEnabled}
              onChange={toggleApi}
            />
          </div>

          {/* Browser row */}
          <div className="flex items-start justify-between gap-4 px-4 py-3.5 transition-colors hover:bg-muted/40">
            <div className="min-w-0 flex-1">
              <Text weight="medium">Browser navigation &amp; in-page requests</Text>
              <Text size="sm" className="text-muted-foreground mt-1">
                Adds trace headers to every Playwright-issued request in browser-mode tests
                (page.goto, in-page fetch/XHR).
              </Text>
            </div>
            <Switch
              aria-label="Toggle browser trace headers"
              isDisabled={disabled}
              isSelected={draft.traceHeadersBrowserEnabled}
              onChange={toggleBrowser}
            />
          </div>
        </div>

        {draft.traceHeadersBrowserEnabled && (
          <div className="rounded-md border border-warning/50 bg-warning/10 p-3">
            <Flex align="start" gap="sm">
              <Warning
                size={18}
                weight="fill"
                className="text-warning shrink-0 mt-0.5"
                aria-hidden
              />
              <div className="space-y-1">
                <Text size="sm" weight="medium">
                  Heads up — your apps must whitelist these headers in CORS.
                </Text>
                <Text size="sm" className="text-muted-foreground">
                  Browser-issued cross-origin requests with a custom header trigger a CORS
                  preflight. If your backend's{' '}
                  <code className="rounded bg-muted/60 px-1 py-0.5 text-xs">
                    Access-Control-Allow-Headers
                  </code>{' '}
                  response does not include{' '}
                  <code className="rounded bg-muted/60 px-1 py-0.5 text-xs">
                    x-canary-flow-id, x-canary-run-id
                  </code>
                  , the browser will block the request and the test will see network failures.
                </Text>
                <Text size="sm" className="text-muted-foreground">
                  Verify your CORS config before enabling this in production.
                </Text>
              </div>
            </Flex>
          </div>
        )}

        {!isAdmin && (
          <Text size="sm" className="text-muted-foreground">
            Only organization admins can update observability settings.
          </Text>
        )}

        <Flex justify="end">
          <Button onPress={saveSettings} isDisabled={disabled}>
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </Flex>
      </div>
    </Box>
  );
}
